import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import FiltersService from '../../services/FiltersService';
import ModelService from '../../services/ModelService';
import ServicesService from '../../services/ServicesService';
import { filterKeys } from '../queries/useFilters';
import { modelKeys } from '../queries/useModels';
import { serviceKeys } from '../queries/useServices';

/**
 * Mutation para salvar dados básicos da model
 */
export function useCompleteModelMeta() {
  return useMutation({
    mutationFn: (data) => ModelService.completeMeta(data),
    onError: (error) => {
      console.error('Erro ao salvar dados básicos:', error);
      toast.error('Erro ao salvar dados básicos');
    },
  });
}

/**
 * Mutation para salvar descrição da model
 */
export function useDescriptionModelMeta() {
  return useMutation({
    mutationFn: (data) => ModelService.descriptionMeta(data),
    onError: (error) => {
      console.error('Erro ao salvar descrição:', error);
      toast.error('Erro ao salvar descrição');
    },
  });
}

/**
 * Mutation para salvar múltiplos filtros
 */
export function useUpsertModelFilters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filters) => {
      // filters é um objeto: { filterId: content, ... }
      const promises = Object.entries(filters).map(([filterId, content]) => {
        // ✅ Converte arrays em string separada por vírgula
        const contentToSave = Array.isArray(content)
          ? content.join(',')
          : content;

        return FiltersService.upsertModelFilter({
          filterId,
          content: contentToSave,
        });
      });

      return Promise.all(promises);
    },
    onSuccess: () => {
      // Invalidar cache de filtros da model
      queryClient.invalidateQueries({
        queryKey: [...filterKeys.all, 'model'],
      });
    },
    onError: (error) => {
      console.error('Erro ao salvar filtros:', error);
      toast.error('Erro ao salvar filtros');
    },
  });
}

/**
 * Mutation para salvar serviços em lote
 */
export function useUpsertModelServices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (services) => ServicesService.upsertModelServiceBatch(services),
    onSuccess: () => {
      // Invalidar cache de serviços da model
      queryClient.invalidateQueries({
        queryKey: serviceKeys.modelServices(),
      });
    },
    onError: (error) => {
      console.error('Erro ao salvar serviços:', error);
      toast.error('Erro ao salvar serviços');
    },
  });
}

/**
 * Hook composto para salvar tudo de uma vez
 */
export function useSaveModelComplete() {
  const completeMeta = useCompleteModelMeta();
  const descriptionMeta = useDescriptionModelMeta();
  const upsertFilters = useUpsertModelFilters();
  const upsertServices = useUpsertModelServices();

  const saveAll = async ({ basicData, descriptionData, filters, services }) => {
    // 1. Salvar dados básicos
    await completeMeta.mutateAsync(basicData);

    // 2. Salvar descrição
    await descriptionMeta.mutateAsync(descriptionData);

    // 3. Salvar filtros (se houver)
    if (filters && Object.keys(filters).length > 0) {
      await upsertFilters.mutateAsync(filters);
    }

    // 4. Salvar serviços (se houver)
    if (services && services.length > 0) {
      await upsertServices.mutateAsync(services);
    }
  };

  return {
    saveAll,
    isLoading:
      completeMeta.isPending ||
      descriptionMeta.isPending ||
      upsertFilters.isPending ||
      upsertServices.isPending,
    isError:
      completeMeta.isError ||
      descriptionMeta.isError ||
      upsertFilters.isError ||
      upsertServices.isError,
    error:
      completeMeta.error ||
      descriptionMeta.error ||
      upsertFilters.error ||
      upsertServices.error,
  };
}

/**
 * ✅ Hook atualizado para usar os serviços corretos
 */
export function useUpdateModelDescription() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ descriptionData, filters, services }) => {
      // 1. Atualizar descrição
      await ModelService.descriptionMeta(descriptionData);

      // 2. Atualizar filtros (se houver)
      if (filters && Object.keys(filters).length > 0) {
        const filterPromises = Object.entries(filters).map(
          ([filterId, content]) => {
            // ✅ Converte arrays em string separada por vírgula
            const contentToSave = Array.isArray(content)
              ? content.join(',')
              : content;

            return FiltersService.upsertModelFilter({
              filterId,
              content: contentToSave,
            });
          }
        );

        await Promise.all(filterPromises);
      }

      // 3. Atualizar serviços (se houver)
      if (services && services.length > 0) {
        await ServicesService.upsertModelServiceBatch(services);
      }

      return { descriptionData, filters, services };
    },
    onSuccess: () => {
      // Invalidar queries relacionadas para refetch
      queryClient.invalidateQueries({ queryKey: ['modelById'] });
      queryClient.invalidateQueries({ queryKey: ['modelFilters'] });
      queryClient.invalidateQueries({ queryKey: ['modelServices'] });
    },
    onError: (error) => {
      console.error('Erro ao atualizar descrição:', error);
      throw error;
    },
  });

  return {
    updateDescription: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}

/**
 * Mutation para registrar visita à página da model
 */
export function useRecordModelVisit() {
  return useMutation({
    mutationFn: (modelId) => ModelService.visitsModel(modelId),
  });
}

/**
 * Mutation para seguir/deixar de seguir model
 */
export function useToggleFollowModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modelId) => ModelService.followModel(modelId),
    onSuccess: (_data, modelId) => {
      // Invalidar status de seguidor (invalidar todas as queries de follower)
      queryClient.invalidateQueries({
        queryKey: [...modelKeys.all, 'follower', modelId],
      });

      // Invalidar informações da model para atualizar contagem de seguidores
      queryClient.invalidateQueries({
        queryKey: modelKeys.info(modelId),
      });

      // Invalidar top para atualizar também se existir
      queryClient.invalidateQueries({
        queryKey: modelKeys.top(modelId),
      });
    },
    onError: (error) => {
      console.error('Erro ao seguir/deixar de seguir:', error);
      toast.error('Erro ao seguir/deixar de seguir');
    },
  });
}
